import React, { useEffect, useState } from 'react';
import {
  Building2,
  MapPin,
  Briefcase,
  Home,
  Clock,
  IndianRupee,
  CalendarDays,
  CheckSquare,
  Star,
  Flame,
  Sparkles,
  Lightbulb,
  GraduationCap,
  Award,
  AlertTriangle,
  X,
  Send,
  CheckCircle2,
  Loader2,
  Video,
} from 'lucide-react';

import { formatSalaryDisplay } from '@/CompanyAdmin/utils/salaryUtils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Skeleton } from '@/Components/ui/skeleton';
import { ScrollArea } from '@/Components/ui/scroll-area';
import { Separator } from '@/Components/ui/separator';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/config/api';

import { getJobDetail, checkApplied } from './publicJobsApi';
import { CandidateLoginDialog } from './CandidateLoginDialog';
import { ApplyDialog } from './ApplyDialog';
import { ThankYouDialog } from './ThankYouDialog';
import { captureApplySource } from './applySource';

/**
 * Detail modal that opens when a candidate clicks a job card.
 *
 * Owns the apply orchestration:
 *   1) Click [Apply] → if not logged in, open CandidateLoginDialog.
 *   2) After login (or if already logged in) → open ApplyDialog.
 *   3) On successful apply → swap to ThankYouDialog.
 *
 * The detail content layout mirrors the recruiter-side JD modal so the
 * visual language is consistent across the product.
 */
export function PublicJobDetailDialog({ jobId, companySlug, onClose }) {
  const flow = useJobApplyFlow(jobId, { onFinished: onClose });
  const { data, loading, error, appliedInfo, onApplyClick } = flow;

  return (
    <>
      {/* The primary dialog steps aside while a login / apply / thank-you
          layer is open. */}
      <Dialog open={!flow.layerOpen} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="max-w-4xl p-0">
          {loading && <LoadingShell />}
          {!loading && error && <ErrorShell error={error} onClose={onClose} />}
          {!loading && !error && data && (
            <JobDetailBody
              job={data}
              applied={appliedInfo}
              onApply={onApplyClick}
              onClose={onClose}
            />
          )}
        </DialogContent>
      </Dialog>

      {flow.layers}
    </>
  );
}

/**
 * useJobApplyFlow — loads one public job and owns the apply orchestration
 * (login → apply form → thank-you). Shared by the detail dialog and the
 * standalone job page so both behave identically.
 *
 * Returns the job (`data`), load state, the candidate's applied state,
 * `onApplyClick`, and `layers`: the login / apply / thank-you dialogs to
 * render alongside. `onFinished` fires when the thank-you layer is closed.
 */
export function useJobApplyFlow(jobId, { onFinished } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [appliedInfo, setAppliedInfo] = useState({
    applied: false,
    logged_in: false,
    applied_at: null,
  });

  // Apply-flow state — only one of these may be true at a time
  const [showLogin, setShowLogin] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [thanks, setThanks] = useState(null);

  // Remember how the visitor reached this job (?src= tag / referring site)
  // so the application can be credited to that channel.
  useEffect(() => {
    captureApplySource(jobId);
  }, [jobId]);

  // ---- fetch job details and applied status -----------------------------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [job, applied] = await Promise.all([
          getJobDetail(jobId),
          checkApplied(jobId).catch(() => ({ applied: false, logged_in: false })),
        ]);
        if (cancelled) return;
        setData(job);
        setAppliedInfo(applied);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load job');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  // ---- handlers ----------------------------------------------------------
  const onApplyClick = () => {
    // If we already know they've applied, do nothing — button is disabled.
    if (appliedInfo.applied) return;
    if (appliedInfo.logged_in) {
      setShowApply(true);
    } else {
      setShowLogin(true);
    }
  };

  const onLoginSuccess = () => {
    // Hand off to the apply dialog — re-check applied state since they
    // may have applied as another account in a previous session.
    setShowLogin(false);
    setAppliedInfo((p) => ({ ...p, logged_in: true }));
    setShowApply(true);
  };

  const onApplied = (response) => {
    setShowApply(false);
    setAppliedInfo({
      applied: true,
      logged_in: true,
      applied_at: response.applied_at,
    });
    setThanks({
      candidate_name: response.candidate_name,
      job_title: response.job_title,
      company_name: response.company_name,
      auto_screen_scheduled: response.auto_screen_scheduled,
      auto_screen_message: response.auto_screen_message,
    });
  };

  const layerOpen = showLogin || showApply || Boolean(thanks);

  const layers = (
    <>
      {/* Login layer */}
      {showLogin && (
        <CandidateLoginDialog
          onClose={() => setShowLogin(false)}
          onSuccess={onLoginSuccess}
        />
      )}

      {/* Apply layer */}
      {showApply && data && (
        <ApplyDialog
          job={data}
          onClose={() => setShowApply(false)}
          onSuccess={onApplied}
        />
      )}

      {/* Thank-you layer */}
      {thanks && (
        <ThankYouDialog
          candidateName={thanks.candidate_name}
          jobTitle={thanks.job_title}
          companyName={thanks.company_name}
          autoScreenScheduled={thanks.auto_screen_scheduled}
          autoScreenMessage={thanks.auto_screen_message}
          onClose={() => {
            setThanks(null);
            onFinished?.();
          }}
        />
      )}
    </>
  );

  return { data, loading, error, appliedInfo, onApplyClick, layers, layerOpen };
}

// ===========================================================================
// Sub-views
// ===========================================================================

function JobDetailBody({ job, applied, onApply, onClose }) {
  return (
    <>
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border/40 bg-card px-6 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <CompanyLogoMark job={job} />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-muted-foreground">{job.company || 'Company'}</p>
              <h1 className="text-xl font-bold tracking-tight">{job.title}</h1>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <JobMetaRow job={job} className="mt-3" />
      </div>

      {/* Body */}
      <ScrollArea className="max-h-[60vh] px-6 py-4">
        <JobSections job={job} />
      </ScrollArea>

      <Separator />
      <DialogFooter className="flex flex-wrap items-center justify-between gap-2 px-6 py-3 sm:flex-nowrap">
        <PostedOn job={job} />
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <ApplyAction applied={applied} onApply={onApply} />
        </div>
      </DialogFooter>
    </>
  );
}

// ===========================================================================
// Shared pieces — also used by the standalone job page (PublicJobPage)
// ===========================================================================

/** Company logo tile; falls back to a building icon when absent or broken. */
export function CompanyLogoMark({ job, className }) {
  return (
    <div
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border/60 bg-card',
        className,
      )}
    >
      {job.company_logo ? (
        <img
          src={`${API_BASE_URL}/api/hirewithus/company/logo/${job.company_logo}`}
          alt={job.company || ''}
          className="h-full w-full object-contain p-0.5"
          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
        />
      ) : null}
      <span
        className="flex h-full w-full items-center justify-center bg-primary/10 text-primary"
        style={{ display: job.company_logo ? 'none' : 'flex' }}
      >
        <Building2 className="h-5 w-5" />
      </span>
    </div>
  );
}

export function PostedOn({ job, className }) {
  return (
    <span className={cn('text-xs text-muted-foreground', className)}>
      Posted{' '}
      {job.created_at
        ? new Date(job.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : ''}
    </span>
  );
}

export function ApplyAction({ applied, onApply, className, size }) {
  return applied.applied ? (
    <Button disabled className={className} size={size}>
      <CheckCircle2 className="mr-2 h-4 w-4" />
      Applied
      {applied.applied_at
        ? ` on ${new Date(applied.applied_at).toLocaleDateString()}`
        : ''}
    </Button>
  ) : (
    <Button onClick={onApply} className={className} size={size}>
      <Send className="mr-2 h-4 w-4" />
      Apply Now
    </Button>
  );
}

export function JobMetaRow({ job, className }) {
  return (
        <div className={cn('flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground', className)}>
          {job.location && <MetaItem icon={MapPin} text={job.location} />}
          {job.employment_type && (
            <MetaItem icon={Briefcase} text={job.employment_type} />
          )}
          {job.job_type && <MetaItem icon={Home} text={job.job_type} />}
          {job.experience_range &&
            job.experience_range !== 'Not specified' && (
              <MetaItem icon={Clock} text={job.experience_range} />
            )}
          {(() => {
            const salaryLabel = formatSalaryDisplay(job.salary_range);
            return salaryLabel !== 'Not specified' ? (
              <MetaItem icon={IndianRupee} text={salaryLabel} />
            ) : null;
          })()}
          {job.application_deadline && (
            <MetaItem
              icon={CalendarDays}
              text={`Apply by ${new Date(
                job.application_deadline,
              ).toLocaleDateString()}`}
            />
          )}
          {job.require_video_resume && (
            <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
              <Video className="h-3 w-3" />
              Video resume required
            </span>
          )}
        </div>
  );
}

export function JobSections({ job }) {
  return (
        <div className="space-y-6">
          {job.job_overview && (
            <Section icon={CheckSquare} title="Job Overview">
              <RichHTML html={job.job_overview} />
            </Section>
          )}

          {job.key_responsibilities && (
            <Section icon={CheckSquare} title="Key Responsibilities">
              <RichHTML html={job.key_responsibilities} />
            </Section>
          )}

          {/* Skills */}
          <Section icon={Star} title="Skills">
            <div className="space-y-3">
              <SkillRow
                label="Required"
                icon={Star}
                color="blue"
                skills={job.required_skills}
              />
              {job.critical_skills?.length > 0 && (
                <SkillRow
                  label="Critical"
                  icon={Flame}
                  color="rose"
                  skills={job.critical_skills}
                />
              )}
              {job.good_to_have_skills?.length > 0 && (
                <SkillRow
                  label="Good to Have"
                  icon={Sparkles}
                  color="emerald"
                  skills={job.good_to_have_skills}
                />
              )}
              {job.soft_skills?.length > 0 && (
                <SkillRow
                  label="Soft Skills"
                  icon={Lightbulb}
                  color="violet"
                  skills={job.soft_skills}
                />
              )}
            </div>
          </Section>

          {job.required_qualifications && (
            <Section icon={GraduationCap} title="Required Qualifications">
              <RichHTML html={job.required_qualifications} />
            </Section>
          )}

          {job.preferred_qualifications && (
            <Section icon={Award} title="Preferred Qualifications">
              <RichHTML html={job.preferred_qualifications} />
            </Section>
          )}

          {job.company_overview && (
            <Section icon={Building2} title="About the Company">
              <RichHTML html={job.company_overview} />
            </Section>
          )}
        </div>
  );
}

// ===========================================================================
// Tiny presentational helpers (parallel the JobDescriptionModal helpers)
// ===========================================================================

function LoadingShell() {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-6 w-1/3" />
      <Skeleton className="h-10 w-2/3" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-24 w-full" />
    </div>
  );
}

function ErrorShell({ error, onClose }) {
  return (
    <div className="flex flex-col items-center gap-3 p-8 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <h3 className="text-lg font-semibold">Job not available</h3>
      <p className="text-sm text-muted-foreground">{error}</p>
      <Button size="sm" onClick={onClose}>
        Close
      </Button>
    </div>
  );
}

function MetaItem({ icon: Icon, text }) {
  if (!text) return null;
  return (
    <span className="flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" />
      <span>{text}</span>
    </span>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="rounded-lg border border-border/40 bg-card p-4">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4 text-primary" />
        {title}
      </h2>
      <div className="text-sm leading-relaxed text-foreground/90">
        {children}
      </div>
    </section>
  );
}

function RichHTML({ html }) {
  if (!html) return null;
  return (
    <div
      className="prose prose-sm max-w-none dark:prose-invert prose-p:my-2 prose-li:my-1"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function SkillRow({ label, icon: Icon, color, skills }) {
  const tones = {
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
    rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
    emerald:
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
    violet:
      'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-400',
  };
  if (!skills || skills.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {skills.map((skill, idx) => (
          <Badge
            key={idx}
            variant="outline"
            className={cn('border-0 text-xs', tones[color])}
          >
            {skill}
          </Badge>
        ))}
      </div>
    </div>
  );
}
